import { toAbsoluteServiceUrl } from '../../lib/absoluteServiceUrl';

const REMOTEGRANT_AGENT_API_PORT = '8754';

export type RemotegrantAgentApiContext = {
  srToken?: string;
};

/** True when API traffic targets the Remotegrant agent backend on port 8754. */
export function isRemotegrantAgentApiBaseUrl(baseURL: string): boolean {
  if (!baseURL) {
    return false;
  }
  try {
    const parsed = new URL(baseURL);
    return parsed.hostname === 'localhost' && parsed.port === REMOTEGRANT_AGENT_API_PORT;
  } catch {
    return /localhost:8754\b/.test(baseURL);
  }
}

export function getRemotegrantAgentWebSocketUrl(): string | undefined {
  // The agent runs outside the page, so a same-origin path must be sent as an absolute URL.
  const value = toAbsoluteServiceUrl(import.meta.env.VITE_APP_WEBSOCKET_URL);
  return value.length > 0 ? value : undefined;
}

function resolveRemotegrantAgentSrToken(context: RemotegrantAgentApiContext): string | undefined {
  const trimmed = context.srToken?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/** Connection fields required by the Remotegrant agent (request body, not query string). */
export function getRemotegrantAgentApiPayloadFields(
  baseURL: string,
  context: RemotegrantAgentApiContext = {},
): Record<string, string> | null {
  if (!isRemotegrantAgentApiBaseUrl(baseURL)) {
    return null;
  }

  const fields: Record<string, string> = {};
  const websocketUrl = getRemotegrantAgentWebSocketUrl();
  if (websocketUrl) {
    fields.websocketUrl = websocketUrl;
  }

  const srToken = resolveRemotegrantAgentSrToken(context);
  if (srToken) {
    fields.srToken = srToken;
  }

  return Object.keys(fields).length > 0 ? fields : null;
}

type RequestPayload = Record<string, unknown> | FormData | undefined;

function mergeFieldsIntoRecord(
  record: Record<string, unknown>,
  fields: Record<string, string>,
): Record<string, unknown> {
  const result = { ...record };
  for (const [key, value] of Object.entries(fields)) {
    if (!Object.hasOwn(result, key)) {
      result[key] = value;
    }
  }
  return result;
}

/** Merges Remotegrant agent fields into axios `data` / request payload. */
export function mergeRemotegrantAgentApiPayload(
  data: RequestPayload,
  baseURL: string,
  context: RemotegrantAgentApiContext = {},
): RequestPayload {
  const fields = getRemotegrantAgentApiPayloadFields(baseURL, context);
  if (!fields) {
    return data;
  }

  if (data instanceof FormData) {
    for (const [key, value] of Object.entries(fields)) {
      if (!data.has(key)) {
        data.append(key, value);
      }
    }
    return data;
  }

  const record =
    typeof data === 'object' && data !== null ? { ...data } : ({} as Record<string, unknown>);
  return mergeFieldsIntoRecord(record, fields);
}

/** JSON body for fetch/XHR when the request has no other payload shape (e.g. GET branding). */
export function buildRemotegrantAgentApiJsonBody(
  baseURL: string,
  context: RemotegrantAgentApiContext = {},
): string | undefined {
  const fields = getRemotegrantAgentApiPayloadFields(baseURL, context);
  return fields ? JSON.stringify(fields) : undefined;
}
